import { useState, useEffect } from "react";
import Input from "../../../components/common/input/Input";
import Select from "../../../components/common/select/Select";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import LanguageTabs, {
  Language,
} from "../../../components/common/tabs/LanguageTabs";
import styles from "./EditProducts.module.scss";
import Button from "../../../components/common/button/Button";
import {
  AtributesTypes,
  EditProductVariantType,
  ErrorsType,
  ProductsVariantType,
} from "../../../api/types/products.types";
import Textarea from "../../../components/common/textarea/Textarea";
import ProductsService from "../../../api/services/products.service";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { toast } from "sonner";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/common/loading/Loading";
import DeleteModal from "../../../components/common/delete-modal/DeleteModal";
import delete_icon from "../../../assets/images/actions/delete.svg";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const EditVariants = () => {
  const { id } = useParams();
  const location = useLocation();
  const { category_id } = location.state || {};
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});
  const [activeLanguage, setActiveLanguage] = useState<Language>("az");
  const [formValues, setFormValues] = useState<{
    [key: number]: Partial<EditProductVariantType>;
  }>({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [errors, setErrors] = useState<{ [key: number]: Partial<ErrorsType> }>(
    {},
  );
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deletedAttributes, setDeletedAttributes] = useState<{
    [key: number]: number[];
  }>({});
  const { t } = useTranslation("translation");
  const navigate = useNavigate();

  const getAttributeName = (attr: AtributesTypes): string => {
    const lang = i18n.language;
    switch (lang) {
      case "az":
        return attr.name_az || attr.name;
      case "en":
        return attr.name_en || attr.name;
      case "es":
        return attr.name_es || attr.name;
      case "de":
        return attr.name_de || attr.name;
      default:
        return attr.name;
    }
  };

  const getAttributeValues = (attr: AtributesTypes) => {
    const lang = i18n.language;
    const valuesMap: Record<string, any[]> = {
      az: attr.values_az || attr.values,
      en: attr.values_en || attr.values,
      es: attr.values_es || attr.values,
      de: attr.values_de || attr.values,
    };

    const valuesArray = valuesMap[lang] || attr.values;
    return (
      valuesArray?.map((v) => ({
        id: String(v.id || v.name),
        name: v.name,
      })) || []
    );
  };

  const { data: variants, loading: variantsLoading } =
    useInfiniteScroll<ProductsVariantType>({
      fetchFn: async (page) => {
        if (!id) return [];
        const res = await ProductsService.getAllVariants(Number(id), page, 20);
        return res.data.data;
      },
      pageSize: 20,
    });

  const { data: atributes } = useInfiniteScroll<AtributesTypes>({
    fetchFn: async (page) => {
      if (!category_id) return [];
      const res = await ProductsService.getCategoriesAttribute(
        Number(category_id),
        page,
        20,
      );
      console.log("attribute response", res.data);
      return res.data;
    },
    pageSize: 20,
    enabled: Boolean(category_id),
    deps: [category_id],
  });

  useEffect(() => {
    if (variants && variants.length > 0) {
      const initialFormValues: {
        [key: number]: Partial<EditProductVariantType>;
      } = {};
      variants.forEach((variant) => {
        const existingJsonData =
          variant.attributes?.map((attr: any) => ({
            attribute_id: attr.attribute_id,
            value: attr.value,
            id: attr.id,
          })) || [];
        const completeJsonData = atributes.map((attr) => {
          const existingAttr = existingJsonData.find(
            (item) => item.attribute_id === attr.id,
          );
          return (
            existingAttr || { attribute_id: attr.id, value: "", id: undefined }
          );
        });

        initialFormValues[variant.id] = {
          name_az: variant.name_az || "",
          name_en: variant.name_en || "",
          name_de: variant.name_de || "",
          name_es: variant.name_es || "",
          description_az: variant.description_az || "",
          description_en: variant.description_en || "",
          description_de: variant.description_de || "",
          description_es: variant.description_es || "",
          sku: variant.sku || "",
          id: variant.id || 0,
          stock_quantity: variant.stock_quantity || 0,
          price: variant.price || 0,
          cost_price: variant.cost_price || 0,
          discount_percent: variant.discount_percent || 0,
          json_data: completeJsonData,
        };
      });
      setFormValues(initialFormValues);
    }
  }, [variants, atributes]);

  const validateForm = (variantId: number): boolean => {
    const newErrors: Partial<ErrorsType> = {};
    const variantForm = formValues[variantId];
    if (!variantForm) {
      return false;
    }
    const isNewVariant = !variants.some((v) => v.id === variantId);
    if (isNewVariant) {
      if (!variantForm.name?.trim()) newErrors.name = t("required.name") || "";
      if (!variantForm.description?.trim())
        newErrors.description = t("required.description") || "";
    } else {
      if (!variantForm.name_en?.trim())
        newErrors.name_en = t("required.name") || "";
      if (!variantForm.description_en?.trim())
        newErrors.description_en = t("required.description") || "";
    }

    if (!variantForm.sku?.trim()) newErrors.sku = t("required.sku") || "";
    if (
      variantForm.stock_quantity === null ||
      variantForm.stock_quantity === undefined
    ) {
      newErrors.stock_quantity = t("required.stock_quantity") || "";
    }
    if (!variantForm.price || Number(variantForm.price) <= 0)
      newErrors.price = t("required.price") || "";
    setErrors((prev) => ({ ...prev, [variantId]: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent, variantId: number) => {
    e.preventDefault();
    if (!validateForm(variantId)) return;
    try {
      setLoading((prev) => ({ ...prev, [variantId]: true }));
      const variantForm = formValues[variantId];
      if (!variantForm) return;
      const formData = new FormData();
      const originalVariant = variants.find((v) => v.id === variantId);
      const isNewVariant = !originalVariant;

      if (isNewVariant) {
        formData.append("name", variantForm.name || "");
        formData.append("description", variantForm.description || "");
      } else {
        const languageFields = [
          "name_az",
          "name_en",
          "name_de",
          "name_es",
          "description_az",
          "description_en",
          "description_de",
          "description_es",
        ];

        languageFields.forEach((field) => {
          const currentValue =
            variantForm[field as keyof EditProductVariantType]?.toString() ||
            "";
          const originalValue =
            originalVariant?.[field as keyof ProductsVariantType]?.toString() ||
            "";
          if (currentValue !== originalValue) {
            formData.append(field, currentValue);
          }
        });
      }
      if (variantForm.sku !== originalVariant?.sku) {
        formData.append("sku", variantForm.sku!);
      }
      if (variantForm.stock_quantity !== originalVariant?.stock_quantity) {
        formData.append("stock_quantity", String(variantForm.stock_quantity));
      }
      if (variantForm.price !== originalVariant?.price) {
        formData.append("price", String(variantForm.price));
      }
      if (variantForm.cost_price !== originalVariant?.cost_price) {
        formData.append("cost_price", String(variantForm.cost_price || 0));
      }
      if (variantForm.discount_percent !== originalVariant?.discount_percent) {
        formData.append(
          "discount_percent",
          String(variantForm.discount_percent || 0),
        );
      }
      const currentJsonData = variantForm.json_data || [];
      const filteredJsonData = currentJsonData.filter(
        (item) => item.value && item.value.trim() !== "",
      );
      if (filteredJsonData.length > 0) {
        formData.append("json_data", JSON.stringify(filteredJsonData));
      }

      if (deletedAttributes[variantId]?.length) {
        const deletedIds = deletedAttributes[variantId].filter(
          (id) => id !== undefined && id !== null,
        );
        if (deletedIds.length > 0) {
          formData.append("delete_data", deletedIds.join(","));
        }
      }

      if (isNewVariant) {
        formData.append("sku", variantForm.sku!);
        formData.append("stock_quantity", String(variantForm.stock_quantity));
        formData.append("price", String(variantForm.price));
        formData.append("cost_price", String(variantForm.cost_price || 0));
        formData.append("product_id", String(id));
        if (filteredJsonData.length > 0) {
          formData.append("json_data", JSON.stringify(filteredJsonData));
        }

        await ProductsService.createProductVariant(formData);
        toast.success(t("products_details.variant_created"), {
          description: t("products_details.variant_created_desc"),
        });
      } else {
        formData.append("product_id", String(id));
        await ProductsService.updateProductVariant(variantId, formData);
        toast.success(t("products_details.variant_updated"), {
          description: t("products_details.variant_updated_desc"),
        });
      }

      setErrors((prev) => ({ ...prev, [variantId]: {} }));
      setDeletedAttributes((prev) => {
        const updated = { ...prev };
        delete updated[variantId];
        return updated;
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    } finally {
      setLoading((prev) => ({ ...prev, [variantId]: false }));
    }
  };

  const handleChange = (
    variantId: number,
    field: keyof EditProductVariantType,
    value: any,
  ) => {
    if (field === "json_data" && Array.isArray(value)) {
      value.forEach((item) => {
        if (item.value && item.id) {
          setDeletedAttributes((prev) => {
            const currentDeleted = prev[variantId] || [];
            if (currentDeleted.includes(item.id)) {
              const updatedDeleted = currentDeleted.filter(
                (id) => id !== item.id,
              );
              return {
                ...prev,
                [variantId]: updatedDeleted,
              };
            }
            return prev;
          });
        }
      });
    }

    setFormValues((prev) => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        [field]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        [field]: "",
      },
    }));
  };
  const getCurrentLanguageValue = (variantId: number, fieldPrefix: string) => {
    const variantForm = formValues[variantId];
    if (!variantForm) return "";

    const isNewVariant = !variants.some((v) => v.id === variantId);

    if (isNewVariant) {
      if (fieldPrefix === "name") return variantForm.name?.toString() || "";
      if (fieldPrefix === "description")
        return variantForm.description?.toString() || "";
      return "";
    } else {
      const fieldName =
        `${fieldPrefix}_${activeLanguage}` as keyof EditProductVariantType;
      return variantForm[fieldName]?.toString() || "";
    }
  };

  const setCurrentLanguageValue = (
    variantId: number,
    fieldPrefix: string,
    value: string,
  ) => {
    const isNewVariant = !variants.some((v) => v.id === variantId);

    if (isNewVariant) {
      if (fieldPrefix === "name") {
        handleChange(variantId, "name", value);
      } else if (fieldPrefix === "description") {
        handleChange(variantId, "description", value);
      }
    } else {
      const fieldName =
        `${fieldPrefix}_${activeLanguage}` as keyof EditProductVariantType;
      handleChange(variantId, fieldName, value);
    }
  };

  const addNewVariant = () => {
    const newVariantId = -Date.now();
    const initialJsonData = atributes.map((attr) => ({
      attribute_id: attr.id,
      value: "",
      id: undefined,
    }));

    setFormValues((prev) => ({
      ...prev,
      [newVariantId]: {
        name: "",
        description: "",
        sku: "",
        stock_quantity: 0,
        price: 0,
        cost_price: 0,
        discount_percent: 0,
        json_data: initialJsonData,
      },
    }));
  };
  const handleDeleteAttribute = (variantId: number, attributeId: number) => {
    const variantForm = formValues[variantId];
    const deletedAttribute = variantForm?.json_data?.find(
      (item) => item.attribute_id === attributeId,
    );
    if (deletedAttribute?.id) {
      setDeletedAttributes((prev: any) => {
        const currentDeleted = prev[variantId] || [];
        if (!currentDeleted.includes(deletedAttribute.id)) {
          const updated = {
            ...prev,
            [variantId]: [...currentDeleted, deletedAttribute.id],
          };
          return updated;
        }
        return prev;
      });
    }

    setFormValues((prev) => {
      const updatedVariant = { ...prev[variantId] };
      updatedVariant.json_data =
        updatedVariant.json_data?.map((item) =>
          item.attribute_id === attributeId ? { ...item, value: "" } : item,
        ) || [];
      return { ...prev, [variantId]: updatedVariant };
    });

    setErrors((prev) => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        [`attribute_${attributeId}`]: "",
      },
    }));
  };
  const handleDelete = async (deleteId: number) => {
    if (variants.some((v) => v.id === deleteId)) {
      try {
        await ProductsService.deleteProductVariant(deleteId, Number(id));
        toast.success(t("products_details.deleted_variant"));
        setFormValues((prev) => {
          const updated = { ...prev };
          delete updated[deleteId];
          return updated;
        });
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          t("error_message");
        toast.error(message);
      } finally {
        setDeleteModal(false);
      }
    } else {
      setFormValues((prev) => {
        const updated = { ...prev };
        delete updated[deleteId];
        return updated;
      });
      setDeleteModal(false);
      toast.info(t("products_details.unsaved_variant_removed"));
    }
  };

  const calculateDiscountedPrice = (variantId: number): number | null => {
    const variantForm = formValues[variantId];
    if (!variantForm) return null;

    const price = Number(variantForm.price) || 0;
    const discountPercent = Number(variantForm.discount_percent) || 0;

    if (discountPercent > 0 && price > 0) {
      const result = price * (1 - discountPercent / 100);
      return Math.round(result * 100) / 100;
    }
    return null;
  };

  if (variantsLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("edit_variants")} />

      <div className={styles.header}>
        <LanguageTabs
          activeLanguage={activeLanguage}
          onLanguageChange={setActiveLanguage}
        />
        <div className={styles.other_btns}>
          <Button type="button" onClick={addNewVariant} icon>
            {t("products_details.add_new_variants")}
          </Button>
          {variants.length !== 0 && (
            <Button
              type="button"
              onClick={() =>
                navigate(`/dashboard/products/create-variant-images/${id}`)
              }
              className={styles.add_variant}
            >
              {t("products_details.add_images")}
            </Button>
          )}
        </div>
      </div>

      {Object.keys(formValues).length === 0 ? (
        <div className={styles.no_variants}>
          <p>{t("products_details.no_variants_found")}</p>
        </div>
      ) : (
        Object.entries(formValues)
          .reverse()
          .map(([variantId, variantForm]) => {
            const isNewVariant = !variants.some(
              (v) => v.id === Number(variantId),
            );
            return (
              <form
                key={variantId}
                className={styles.variant_form}
                onSubmit={(e) => handleSubmit(e, Number(variantId))}
              >
                <div className={styles.variant_header}>
                  <h3>
                    {isNewVariant
                      ? t("products_details.new_variant")
                      : t("products_details.variant")}
                    {!isNewVariant &&
                      variants?.find((v) => v.id === Number(variantId))?.sku &&
                      ` - SKU: ${
                        variants.find((v) => v.id === Number(variantId))?.sku
                      }`}
                  </h3>

                  <div
                    onClick={() => {
                      setDeleteId(Number(variantForm.id) || Number(variantId));
                      setDeleteModal(true);
                    }}
                    className={styles.delete_btn}
                  >
                    <img src={delete_icon} alt="del" />
                  </div>
                </div>
                <div className={styles.grid}>
                  <div className={styles.left}>
                    <div className={styles.form_category}>
                      <p className={styles.title}>
                        {t("products_details.product_info")}
                      </p>
                      <div className={styles.form_content_column}>
                        <Input
                          className={styles.input}
                          label={`${t("labels.name")} ${
                            isNewVariant ? "" : activeLanguage.toUpperCase()
                          }`}
                          placeholder={t("labels.name") || ""}
                          value={getCurrentLanguageValue(
                            Number(variantId),
                            "name",
                          )}
                          onChange={(e) =>
                            setCurrentLanguageValue(
                              Number(variantId),
                              "name",
                              e.target.value,
                            )
                          }
                          error={
                            isNewVariant
                              ? errors[Number(variantId)]?.name
                              : errors[Number(variantId)]?.name_en
                          }
                        />
                        <Textarea
                          className={styles.input}
                          label={`${t("labels.description")} ${
                            isNewVariant ? "" : activeLanguage.toUpperCase()
                          }`}
                          placeholder={t("labels.description") || ""}
                          value={getCurrentLanguageValue(
                            Number(variantId),
                            "description",
                          )}
                          onChange={(e) =>
                            setCurrentLanguageValue(
                              Number(variantId),
                              "description",
                              e.target.value,
                            )
                          }
                          error={
                            isNewVariant
                              ? errors[Number(variantId)]?.description
                              : errors[Number(variantId)]?.description_en
                          }
                          maxLength={500}
                          showCount={true}
                        />
                        <Input
                          className={styles.input}
                          label={t("labels.sku") || ""}
                          placeholder="eg. 242425252"
                          value={variantForm.sku || ""}
                          onChange={(e) =>
                            handleChange(
                              Number(variantId),
                              "sku",
                              e.target.value,
                            )
                          }
                          error={errors[Number(variantId)]?.sku}
                        />
                        <Input
                          className={styles.input}
                          label={t("labels.stock_quantity") || ""}
                          placeholder={t("labels.stock_quantity") || ""}
                          type="number"
                          value={variantForm.stock_quantity}
                          onChange={(e) =>
                            handleChange(
                              Number(variantId),
                              "stock_quantity",
                              e.target.value,
                            )
                          }
                          error={errors[Number(variantId)]?.stock_quantity}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.right}>
                    <div className={styles.form_category}>
                      <p className={styles.title}>
                        {t("products_details.pricing")}
                      </p>
                      <div className={styles.form_content_column}>
                        <Input
                          className={styles.input}
                          label={t("labels.cost_price") || ""}
                          type="number"
                          placeholder={t("labels.cost_price") || ""}
                          value={variantForm.cost_price ?? ""}
                          onChange={(e) =>
                            handleChange(
                              Number(variantId),
                              "cost_price",
                              e.target.value,
                            )
                          }
                        />
                        <p className={styles.info}>
                          {t("labels.cost_price_hint")}
                        </p>
                        <Input
                          className={styles.input}
                          label={t("labels.price") || ""}
                          type="number"
                          placeholder={t("labels.price") || ""}
                          value={variantForm.price}
                          onChange={(e) =>
                            handleChange(
                              Number(variantId),
                              "price",
                              e.target.value,
                            )
                          }
                          error={errors[Number(variantId)]?.price}
                        />
                        <Input
                          className={styles.input}
                          label={t("labels.discount_percent") || ""}
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          placeholder={t("labels.discount_percent") || ""}
                          value={variantForm.discount_percent || ""}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "") {
                              handleChange(
                                Number(variantId),
                                "discount_percent",
                                "",
                              );
                              return;
                            }

                            const numberValue = Math.min(
                              100,
                              Math.max(0, Number(value)),
                            );

                            handleChange(
                              Number(variantId),
                              "discount_percent",
                              numberValue,
                            );
                          }}
                        />
                        {calculateDiscountedPrice(Number(variantId)) !==
                          null && (
                          <p className={styles.discounted}>
                            {t("labels.discounted_price")}:{" "}
                            <strong>
                              {calculateDiscountedPrice(Number(variantId))}
                            </strong>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={styles.form_category}>
                      <p className={styles.title}>
                        {t("products_details.attributes")}
                      </p>
                      <div className={styles.form_content_column}>
                        {atributes.length > 0 &&
                          atributes.map((attr) => {
                            const currentAttribute =
                              variantForm.json_data?.find(
                                (i) => i.attribute_id === attr.id,
                              );
                            const currentValue = currentAttribute?.value || "";
                            const isAttributeDeleted = currentAttribute?.id
                              ? deletedAttributes[Number(variantId)]?.includes(
                                  currentAttribute.id,
                                )
                              : false;

                            return (
                              <div className={styles.atributes} key={attr.id}>
                                <Select
                                  label={getAttributeName(attr)}
                                  value={currentValue}
                                  onChange={(value) => {
                                    if (!value) return;
                                    const { id, name } = value;
                                    const currentJsonData =
                                      variantForm.json_data || [];
                                    const attributeExists =
                                      currentJsonData.some(
                                        (item) => item.attribute_id === attr.id,
                                      );

                                    const newJsonData = attributeExists
                                      ? currentJsonData.map((item) =>
                                          item.attribute_id === attr.id
                                            ? {
                                                ...item,
                                                value: name,
                                                id: item.id,
                                              }
                                            : item,
                                        )
                                      : [
                                          ...currentJsonData,
                                          {
                                            attribute_id: attr.id,
                                            value: name,
                                            id: undefined,
                                          },
                                        ];
                                    handleChange(
                                      Number(variantId),
                                      "json_data",
                                      newJsonData,
                                    );
                                    if (isAttributeDeleted && name) {
                                      setDeletedAttributes((prev) => {
                                        const currentDeleted =
                                          prev[Number(variantId)] || [];
                                        const updatedDeleted =
                                          currentDeleted.filter(
                                            (deletedId) =>
                                              deletedId !==
                                              currentAttribute?.id,
                                          );
                                        return {
                                          ...prev,
                                          [Number(variantId)]: updatedDeleted,
                                        };
                                      });
                                    }
                                  }}
                                  options={getAttributeValues(attr)}
                                  className={styles.input}
                                />
                                <div
                                  className={styles.del_atribute}
                                  onClick={() => {
                                    if (isAttributeDeleted) {
                                      return;
                                    }
                                    handleDeleteAttribute(
                                      Number(variantId),
                                      attr.id,
                                    );
                                  }}
                                  style={{
                                    opacity: isAttributeDeleted ? 0.5 : 1,
                                    cursor: isAttributeDeleted
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <img src={delete_icon} alt="del" />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.variant_actions}>
                  <Button type="submit" disabled={loading[Number(variantId)]}>
                    {loading[Number(variantId)]
                      ? t("buttons.loading")
                      : t("submit")}
                  </Button>
                  {!isNewVariant && (
                    <Button
                      type="button"
                      onClick={() => {
                        navigate(
                          `/dashboard/products/edit-variant-images/${id}`,
                          {
                            state: { variant_id: variantForm.id },
                          },
                        );
                      }}
                      className={styles.add_variant}
                    >
                      {t("products_details.images")}
                    </Button>
                  )}
                </div>
              </form>
            );
          })
      )}
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("products_details.delete_variant") || ""}
      />
    </div>
  );
};

export default EditVariants;
